import {
  deLang as _deLang,
  enLang as _enLang,
  frLang as _frLang,
  nlLang as _nlLang,
  csLang as _csLang,
  itLang as _itLang,
  zhLang as _zhLang,
  faLang as _faLang,
  Validator as FacileValidator
} from '@upjs/facile-validator';
import * as rules from '@upjs/facile-validator/dist';
import { default as _I18N } from "@dia-ui/util_i18n";

/**
 * internal types
 */
type ErrorCause = typeof rules;
type FieldArgTypes = string | number | RegExp;
type RichXRule = {
    errorMessage?: string | ((field: FormInputElement) => string);
    value: FieldArgTypes;
};
type LangKeys = ErrorCause[keyof ErrorCause];
// @ts-ignore
type Lang = Partial<Record<LangKeys, string>>;
type XRules = Record<string, FieldArgTypes | RichXRule>;

/**
 * ErrorDetail
 */
interface ErrorDetail {
    element: FormInputElement;
    rule: string;
    cause: string;
    message: string;
    args: string[];
}

/**
 * Events
 */
interface Events {
    'validation:start': (container: HTMLElement) => void;
    'validation:end': (container: HTMLElement, isSuccessful: boolean) => void;
    'validation:success': (container: HTMLElement) => void;
    'validation:failed': (container: HTMLElement) => void;
    'field:error': (container: HTMLElement, element: FormInputElement, errors: ErrorDetail[]) => void;
}

/**
 * ValidatorOptions
 */
interface ValidatorOptions {
    lang?: Lang;
    on?: Partial<Events>;
    renderErrors?: boolean;
    xRules?: XRules;
    onFieldChangeValidation?: boolean;
    onFieldChangeValidationDelay?: number;
}

/**
 * FormInputElement
 */
export type FormInputElement = HTMLInputElement | HTMLSelectElement;

/**
 * RuleError
 */
class RuleError {
    message;

    constructor(message, ...args) {
        this.message = message;
        //@ts-ignore
        this.args = args;
    }
}

/**
 * Validator
 * this class extends the FacileValidator to add some expression parsing for math expression of known fields by name
 *
 * example: we have two fields width and height
 * <input name="width" data-rules="required|max:500 - height" /> <-- max value is calculated before validation on every change
 * <input name="height" data-rules="required|max:200" />
 */
// @ts-ignore
export class Validator extends FacileValidator {
    static PREPROCESSING: boolean = false;
    initialRules: any;

    /**
     * constructor
     * @param container
     * @param options
     */
    constructor(container: HTMLElement, options: ValidatorOptions = {}) {
        super(container, options);
    }

    /**
     * validate
     * @param fields
     * @param shouldFireResultsEvent
     */
    public validate(fields?: NodeListOf<FormInputElement> | FormInputElement[], shouldFireResultsEvent: boolean = true): boolean {
        this.events.call("validation:preprocess", this.container);

        /**
         * preprocess all fields
         */
        let recursiveFields= this.handleRecursiveFieldRules(fields),
          _fields = Array.from(fields).filter(field => !recursiveFields.includes(field)),

          processFields = this.preProcessFieldRules([...recursiveFields, ..._fields], true),
          preFields = this.preProcessFieldRules(processFields);

        /**
         * after preprocess we call the facile validator
         */
        return super.validate(preFields, shouldFireResultsEvent);
    }

    /**
     * handleRecursiveFieldRules
     * @private
     * @param fields
     */
    private handleRecursiveFieldRules(fields) {
      let matches = [];

      fields.forEach(field => {
        let currentName = field.getAttribute("name"),
          currentRules = field.getAttribute("data-rules");

        let regularExpression: RegExp = new RegExp("(?<!-)" + currentName + "(?!-)(?!:)", 'g'),
          _matches: RegExpMatchArray = currentRules.match(regularExpression);

        if (_matches?.length > 0) {
          matches.push(field);
        }
      });

      return matches;
    }

    /**
     * preProcessFieldRules
     * @param fields
     * @param isRecursive
     * @protected
     */
    protected preProcessFieldRules(fields: any, isRecursive: boolean=false): any {
        /**
         * we need to save initial rule string to
         * process the expression on every field change
         * with the current values
         */
        if (this.initialRules === undefined) {
            this.initialRules = {};
        }

        let _fields = Array.from(fields),
            ruleAttributeName: string = "data-rules";

        /**
         * iterate through all known fields by validator
         */
        _fields.forEach((field: any, fieldIndex: number): void => {
            let fieldName = field.getAttribute("name"),
                rules: string;

            /**
             * if initial rule exists lets catch it
             */
            if (this.initialRules[fieldName] !== undefined) {
                rules = this.initialRules[fieldName];
            /**
             * if initial rule not exists lets create it
             */
            } else {
                rules = field.getAttribute(ruleAttributeName);
                this.initialRules[fieldName] = rules;
            }

            /**
             * we have rules? let's split them
             */
            if (rules) {
                let ruleSeparator: string = "|",
                    splitRules: string[] = rules.split(ruleSeparator),
                    isNumber: boolean = rules.indexOf("number" + ruleSeparator) !== -1 || rules.indexOf(ruleSeparator + "number") !== -1;

                /**
                 * now each rule as to be split by value separator
                 */
                splitRules.forEach((rule: string, ruleIndex: number) => {
                    let ruleNameValueSeparator: string = ":",
                        ruleNameValue: string[] = rule.split(ruleNameValueSeparator),
                        ruleName: string = ruleNameValue[0],
                        ruleValue: any = ruleNameValue[1];

                    /**
                     * if we have a value, we can preprocess it
                     */
                    if (ruleValue) {
                        ruleValue = this.preProcessKnownFieldValuesInRules(field, ruleName, ruleValue, _fields, isRecursive);

                        /**
                         * if ruleValue is Infinity we set it to zero
                         */
                        if (isNumber && ruleValue === Infinity) {
                            ruleValue = 0;
                        }

                        /**
                         * if the resulting ruleValue is a number we should cast it to it type
                         */
                        if (isNumber && !Number.isNaN(ruleValue)) {
                            ruleValue = +ruleValue;

                          /**
                           *  if we have a step rule, we execute it on our numeric value
                           */
                          if (rules.indexOf("step:") !== -1) {
                              let splitStepRule = rules.split("step:")[1].split(ruleSeparator)[0],
                                splitStepRuleValue = Number.parseFloat(splitStepRule);

                              ruleValue = ruleValue.toFixed(Validator.countDecimals(splitStepRuleValue));
                            }
                        }

                        /**
                         * after preprocessing we write ruleName
                         * and ruleValue back to its original index
                         * for validation by facile
                         */
                        splitRules[ruleIndex] = ruleName.concat(ruleNameValueSeparator, ruleValue)
                    }
                });

                /**
                 * know we join back splitRules
                 */
                let rulesString: string = splitRules.join(ruleSeparator),
                    updateField = fields instanceof NodeList ? fields.item(fieldIndex) : fields[fieldIndex];

                /**
                 * at the end we check if there is an update in data-rules
                 * after preprocessing and if it's the case we write the
                 * updated and joined string back to the attribute
                 */
                if (rulesString !== updateField.getAttribute(ruleAttributeName) && rulesString.indexOf("NaN") === -1) {
                    Validator.PREPROCESSING = true;
                    updateField.setAttribute(ruleAttributeName, rulesString);
                    setTimeout(() => {
                        Validator.PREPROCESSING = false;
                    }, 350);
                }
            }
        });

        return fields;
    }

    /**
     * preProcessKnownFieldValuesInRules
     * @param currentField
     * @param ruleName
     * @param ruleValue
     * @param fields
     * @param isRecursive
     * @protected
     */
    protected preProcessKnownFieldValuesInRules(currentField, ruleName: string, ruleValue: any, fields: any, isRecursive: boolean=false): any {
        if (typeof ruleValue !== "string") return ruleValue;

        let matches= this.getMatchesForKnownFieldsInExpression(ruleValue, fields);

        if (Object.keys(matches).length > 0) {
            // before this we need to validate an replace recursive if a matched field has also rules
            ruleValue = this.replaceAndExecuteKnownFields(currentField, ruleName, ruleValue, matches, fields, isRecursive);
        }

        return ruleValue;
    }

    /**
     * replaceAndExecuteKnownFields
     * @param currentField
     * @param ruleName
     * @param ruleValue
     * @param knownFields
     * @param fields
     * @param isRecursive
     * @private
     */
    private replaceAndExecuteKnownFields(currentField, ruleName, ruleValue, knownFields: any, fields: any, isRecursive: boolean=false) {
        let errors = [];

        Object.keys(knownFields).forEach((knownFieldName) => {
            // check if field is empty
            let knownFieldValue = knownFields[knownFieldName],
                knownFieldReference = fields.find(field => field.getAttribute("name") === knownFieldName);

            if (knownFieldReference) {
                if (!isRecursive) {
                  this.preProcessFieldRules([knownFieldReference], knownFieldName === currentField.getAttribute("name"));
                }

                if (this.getFieldValue(knownFieldReference) === "") {
                    let error = new RuleError(this.getFieldLabelDescription(knownFieldReference) + " bitte vorher ausfüllen!");

                    this.validatorError.setError(knownFieldReference, ruleName, error);
                    errors.push(error);
                } else {
                    /**
                     * if value can be executed each rule lets do it otherwise return it as it is
                     */
                    ruleValue = ruleValue.replace(new RegExp("(?<!-)" + knownFieldName + "(?!-)(?!:)", 'g'), knownFieldValue);
                }
            }
        });

        this.handleErrors(currentField, errors);

        /**
         * at the end we have to execute the complete ruleValue
         * because there could be static numbers in rule string
         */
        ruleValue = Validator.executeValue(ruleValue);

        if (ruleName === "required_if") {
            ruleValue = typeof ruleValue !== "boolean" ? Boolean(ruleValue) : ruleValue;
        }

        return ruleValue;
    }

    /**
     * getMatchesForKnownFieldsInExpression
     * @private
     * @param ruleValue
     * @param knownFields
     */
    private getMatchesForKnownFieldsInExpression(ruleValue: string, knownFields: any) {
        let knownFieldValuesByName = this.getFieldValuesByName(knownFields),
            matches = {};

        Object.keys(knownFieldValuesByName).forEach(knownFieldName => {
            let regularExpression: RegExp = new RegExp("(?<!-)" + knownFieldName + "(?!-)(?!:)", 'g'),
            _matches: RegExpMatchArray = ruleValue.match(regularExpression);

            if (_matches?.length > 0) {
                matches[knownFieldName] = knownFieldValuesByName[knownFieldName];
            }
        });

        return matches;
    }

    /**
     * getFieldLabelDescription
     * @param field
     * @private
     */
    private getFieldLabelDescription(field: HTMLElement): string {
        let area = field.closest('dia-form-area'),
            label = field.nextElementSibling,
            labelText: string = "";

        if (area) {
            let headline = area.querySelector('h3').innerHTML;
            labelText += headline + " - ";
        }

        labelText += label.innerHTML;

        return labelText;
    }

    /**
     * handleErrors
     * @param field
     * @param errors
     * @private
     */
    private handleErrors(field, errors) {
        let _message = "";

        errors.forEach(error => {
            _message += (!_message ? "" : "\n   ") + "- " + (field.getAttribute("data-validator-message-" + error.rule) || error.message);
        });

        if (_message.length > 0) {
            field.parentElement.setAttribute("data-validator-errors", _message);
        }
    }

    /**
     * getFieldValuesByName
     * get all values by field name to replace them in expression
     * @param fields
     * @private
     */
    protected getFieldValuesByName(fields: any): any {
        let allFieldValuesByName = {};

        /**
         * iterate all known fields by validator
         */
        fields.forEach((current: HTMLElement) => {
            let fieldName = current.getAttribute("name");

            allFieldValuesByName[fieldName] = this.getFieldValue(current);
        });

        return allFieldValuesByName;
    }

    /**
     * getFieldValue
     * @param element
     * @private
     */
    private getFieldValue(element: HTMLElement): string {
        if (element instanceof HTMLInputElement) {
            if (element.type === "checkbox" || element.type === "radio") {
                return element.checked ? "checked" : "";
            }

            return element.value;
        }

        if (element instanceof HTMLTextAreaElement) {
            return element.value;
        }

        if (element instanceof HTMLSelectElement) {
            return Array.from(element.selectedOptions).map((option) => option.value).join(",");
        }

        return "";
    }

    /**
     * executeValue
     * @param value
     */
    static executeValue(value: string): any {
        /**
         * this executes a possible expression in a new function context for safety reasons
         * this way we have two benefits. The first is that we can use eval in only this context
         * without the possibility to hack us and grab the window or document context because
         * it is encapsulated. The second benefit is that we can "use strict" to limit
         * the context of this function with every expression to its own to prevent a breakout
         */
        return new Function("value",
            '"use strict"; let _return; try { _return = eval(value); } catch(e) { _return = value; } return _return;'
        )(value);
    }

    static countDecimals(value) {
        if ((value % 1) != 0) {
            return value.toString().split(".")[1].length;
        }

        return 0;
    }

    /**
     * fmod
     * @param val
     * @param step
     */
    static fmod(val: number, step: number): number {
        let valDecCount: number = (val.toString().split('.')[1] || '').length,
            stepDecCount: number = (step.toString().split('.')[1] || '').length,
            decCount: number = valDecCount > stepDecCount? valDecCount : stepDecCount,
            valInt: number = parseInt(val.toFixed(decCount).replace('.','')),
            stepInt: number = parseInt(step.toFixed(decCount).replace('.',''));

        return (valInt % stepInt) / Math.pow(10, decCount);
    };

    /**
     * getLang
     * @param lang
     */
    static async getLang(lang: string) {
      let I18N = new _I18N();
      await I18N.initialize(lang, "/dist/collection/behaviors/validator/assets/i18n/");

      const customLang = {
        "step": I18N.t("Bitte geben Sie einen Wert in '$1er' Schritten an z.B. $2"),
        "max-value": I18N.t("Bitte geben Sie einen Wert kleiner oder gleich $1er ein!")
      };

      const deLang = { ..._deLang, ...customLang};
      const enLang = { ..._enLang, ...customLang};
      const frLang = { ..._frLang, ...customLang};
      const nlLang = { ..._nlLang, ...customLang};
      const csLang = { ..._csLang, ...customLang};
      const itLang = { ..._itLang, ...customLang};
      const zhLang = { ..._zhLang, ...customLang};
      const faLang = { ..._faLang, ...customLang};

      let langs = {
        "de": deLang,
        "en": enLang,
        "gb": enLang, // remove if shop uses locale string in iso format en and not invalid gb
        "fr": frLang,
        "nl": nlLang,
        "cs": csLang,
        "it": itLang,
        "zh": zhLang,
        "fa": faLang
      };

      if (!langs[lang]) {
        console.error("cant get lang '" + lang + "', its not defined by validator behavior");
      }

      return langs[lang];
    };
}

//@ts-ignore
FacileValidator.RuleErrorCustom = Validator.RuleErrorCustom = RuleError;

/**
 * extend base rules
 */
FacileValidator.rules = Validator.rules = {
    ...FacileValidator.rules,
    step: function step(value, step): boolean | RuleError {
        value = +value;
        step = +step;

        let _fmod= Validator.fmod(value, step),
            floorValue= Number(Math.floor(value / step) * step).toFixed(Validator.countDecimals(step));

        if (_fmod === 0) {
            return true;
        }

        return new RuleError("step", String(step), String(floorValue));
    },
    maxValue: function maxValue(value, maxValue): boolean | RuleError {
        if (Number.parseFloat(value) > Number.parseFloat(maxValue)) return new RuleError("max-value", String(maxValue));

        return true;
    },
    required_if: function required_if(value, statement): boolean | RuleError {
      let executedStatement = Validator.executeValue(statement);

      if (typeof executedStatement !== "boolean") {
          executedStatement = Boolean(executedStatement);
      }

      // check if statement is valid and bool
      // if it´s true lets check value because its required
      if (executedStatement) {
        // if we have a value
        if (value && value.trim().length > 0) {
          return true;
        // if no value throw required error message
        } else {
          return new RuleError("required", value);
        }
      // value not required
      } else {
        // so lets return true
        return true;
      }
    }
};
